import React, { useState } from 'react'
import { find } from 'lodash'
import styled from 'styled-components'
import { Spinner } from '../../../../../components/generic'
import RiskAssessmentItem from './RiskAssessmentItem'
import RiskAssessmentSortType from './RiskAssessmentSortType'
import { colors } from '@lmc/common/constants'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from '../../../../../locale'
import { riskAssessmentsConstants } from '../../../../../locale/riskAssessments'
import { Carer, Resident, RiskAssessment } from '@lmc/ts-common'
import Chevron from '@lmc/ui-kit/src/systemIcons/Chevron/Chevron'

interface Params {
  risk_assessment_id?: string
}

interface RiskAssessmentsSectionProps {
  title: string
  carers?: Carer[]
  sortType?: string
  carePlanNominative?: string
  params: Params
  riskAssessments: RiskAssessment[]
  loading?: boolean
  onRiskAssessmentClick: (id: string) => void
  setSortType: (sortType: string) => void
  initialExpanded?: boolean
  emptyStateConfig?: {
    helpMessage: string
    supportArticle: string
  }
}

const RiskAssessmentsSection: React.FC<RiskAssessmentsSectionProps> = ({
  title,
  carers,
  sortType,
  carePlanNominative,
  params,
  riskAssessments,
  loading,
  onRiskAssessmentClick,
  setSortType,
  initialExpanded = true,
  emptyStateConfig
}) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS)
  const [isExpanded, setIsExpanded] = useState(initialExpanded)

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const renderEmptyState = () => {
    if (!emptyStateConfig) {
      return null
    }

    return (
      <EmptyState
        title={t(riskAssessmentsConstants.NOTHING_TO_SEE_HERE)}
        description={emptyStateConfig.helpMessage}
        icon={<NoDataIcon />}
      >
        <EmptyMessageLink
          href={emptyStateConfig.supportArticle}
          rel="noreferrer"
          target="_blank"
        >
          {t(riskAssessmentsConstants.FIND_OUT_MORE)}
        </EmptyMessageLink>
      </EmptyState>
    )
  }

  const renderSortType = () => {
    return (
      <SortContainer $hasPaddingTop={!params.risk_assessment_id}>
        <SortByLabel>{t(riskAssessmentsConstants.SORT_BY)}</SortByLabel>
        <RiskAssessmentSortType
          params={params}
          onChange={setSortType}
          sortType={sortType}
        />
        <ChevronButton onClick={toggleExpanded} data-testid="chevron-button">
          <Chevron direction={isExpanded ? 'up' : 'down'} />
        </ChevronButton>
      </SortContainer>
    )
  }

  const Section = riskAssessments.length
    ? riskAssessments.map((assessment: RiskAssessment) => {
        const carer = find(carers, { id: assessment.createdBy })
        return (
          <RiskAssessmentItem
            carer={carer}
            key={assessment.id}
            riskAssessment={assessment}
            onView={onRiskAssessmentClick}
            carePlanNominative={carePlanNominative}
          />
        )
      })
    : renderEmptyState()

  return (
    <SectionContainer>
      <SectionTitle>
        {title}
        {renderSortType()}
      </SectionTitle>
      {isExpanded &&
        (loading || !sortType ? (
          <Spinner />
        ) : (
          <div data-testid="risk-assessments-content">{Section}</div>
        ))}
    </SectionContainer>
  )
}

const SectionContainer = styled.div`
  padding-top: 10px;
  padding-bottom: 30px;
`

const SectionTitle = styled.div`
  border-bottom: 1px solid ${colors.grey};
  font-size: 16px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`

const SortByLabel = styled.span`
  color: ${colors.darkgrey};
  display: block;
  margin-right: 5px;
`

const SortContainer = styled.div<{ $hasPaddingTop?: boolean }>`
  align-items: baseline;
  display: flex;
  justify-content: flex-end;
  ${({ $hasPaddingTop }) =>
    $hasPaddingTop ? 'padding-top: 30px;' : 'padding-top: 10px;'}
`

const ChevronButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px 0px;
  margin-left: 8px;
  display: flex;
  align-items: center;
  color: ${colors.darkgrey};

  &:hover {
    color: ${colors.black};
  }
`

const EmptyMessageLink = styled.a`
  margin-left: 5px;
`

export default RiskAssessmentsSection
